import React from "react";
import {
  Typography,
  tokens,
  Pill,
  SvgNames,
  Banner,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { useMediaQuery } from "react-responsive";
import { mobileMaxWidth } from "../../../components/atoms/GlobalStyles";
import type { DesignPreferenceItemFormatted } from "../../types/designPreferences";
import {
  getDesignPreferenceValue,
  isEffectivelyRequired,
  isFillableDesignPreferenceField,
} from "../../utils/designPreferenceConditions";
import {
  Card,
  CardHeader as BaseCardHeader,
  CardContent,
  CardSectionDivider,
} from "./Card";
import { DesignPreferenceSection } from "./DesignPreferenceSection";
import { ViewMode } from "checkout/types/payments-ui-form";

type DesignPreferenceGroup = {
  header: DesignPreferenceItemFormatted;
  sections: {
    subHeader?: DesignPreferenceItemFormatted;
    fields: DesignPreferenceItemFormatted[];
  }[];
};

type DesignPreferenceCardProps = {
  group: DesignPreferenceGroup;
  viewMode: ViewMode;
  formValues: Map<string, string>;
  onFieldChange: (
    item: DesignPreferenceItemFormatted,
    index: number,
    value: string
  ) => void;
  fieldIndices: Map<DesignPreferenceItemFormatted, number>;
};

const DesignPreferenceCard: React.FC<DesignPreferenceCardProps> = ({
  group,
  viewMode,
  formValues,
  onFieldChange,
  fieldIndices,
}) => {
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });

  const isBrightBoxCard = group.header.question
    .toLowerCase()
    .includes("brightbox backup preferences");

  const allFieldsInGroup = group.sections.flatMap((section) => section.fields);

  const getValue = (field: DesignPreferenceItemFormatted): string =>
    getDesignPreferenceValue(field, viewMode, fieldIndices, formValues);

  const getFieldValue = (field: DesignPreferenceItemFormatted): string =>
    getValue(field);

  const isFillableField = isFillableDesignPreferenceField;

  const getFillableFields = (
    sections: DesignPreferenceGroup["sections"],
    additionalFilter?: (field: DesignPreferenceItemFormatted) => boolean
  ): DesignPreferenceItemFormatted[] => {
    return sections
      .flatMap((section) => section.fields)
      .filter((field) => {
        if (!isFillableField(field)) {
          return false;
        }
        return additionalFilter ? additionalFilter(field) : true;
      });
  };

  const checkFieldsAreFilled = (
    fields: DesignPreferenceItemFormatted[],
    emptyFieldsAreComplete: boolean = false
  ): boolean => {
    if (fields.length === 0) {
      return emptyFieldsAreComplete;
    }
    return fields.every((field) => getFieldValue(field).trim() !== "");
  };

  const getIsAllFieldsFilled = (): boolean => {
    const fillableFields = getFillableFields(group.sections);
    const fieldsThatMustBeComplete = fillableFields.filter((field) =>
      isEffectivelyRequired(field, allFieldsInGroup, getValue)
    );
    if (fieldsThatMustBeComplete.length > 0) {
      return checkFieldsAreFilled(fieldsThatMustBeComplete, false);
    }
    return checkFieldsAreFilled(fillableFields, false);
  };

  const isAllFieldsFilled = getIsAllFieldsFilled();

  return (
    <Card>
      <StyledCardHeader isMobile={isMobile}>
        <Typography
          lineHeight={24}
          size={tokens.FONT_SIZE_3}
          color={tokens.BRAND_HEROBLUE}
        >
          {group.header.question}
        </Typography>
        <StyledPill
          text={isAllFieldsFilled ? "Completed" : "Ready to complete"}
          type={isAllFieldsFilled ? "success" : "info"}
          {...(isAllFieldsFilled ? {} : { icon: SvgNames.ClockCountdown })}
        />
      </StyledCardHeader>
      <CardSectionDivider />
      <CardContent>
        {isBrightBoxCard && (
          <Banner color="info" show={true} showClose={false}>
            Information needed for a seamless day of install and for system
            monitoring after PTO.
          </Banner>
        )}
        {group.sections.map((section, index) => (
          <DesignPreferenceSection
            key={index}
            allFieldsInGroup={allFieldsInGroup}
            subHeader={section.subHeader}
            fields={section.fields}
            viewMode={viewMode}
            formValues={formValues}
            onFieldChange={onFieldChange}
            fieldIndices={fieldIndices}
          />
        ))}
      </CardContent>
    </Card>
  );
};

const StyledCardHeader = styled(BaseCardHeader)<{ isMobile?: boolean }>`
  margin: 20px 24px;
  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column;
    align-items: flex-start;
  `}
`;

const StyledPill = styled(Pill)`
  height: 30px;

  & > p {
    font-weight: 500;
  }
`;

export { DesignPreferenceCard };
